import { useState, useEffect } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, SectionLabel, Pill } from "@/components/common";
import { Settings as SettingsIcon, RefreshCw, Mail, Clock, CreditCard, Crown, Volume2 } from "lucide-react";
import { Link } from "react-router-dom";
import { PlanBadge } from "@/components/app/UpgradeGate";
import { soundEnabled, setSoundEnabled, playAlertChime } from "@/lib/sound";

export default function SettingsPage() {
  const { user, refreshUser, plan: userPlan, ent } = useAuth();
  const [plan, setPlan] = useState(null);
  const [emailAlerts, setEmailAlerts] = useState(!!user?.email_alerts);
  const [weekly, setWeekly] = useState(!!user?.weekly_digest);
  const [qh, setQh] = useState({ enabled: false, start: "22:00", end: "07:00" });
  const [sessions, setSessions] = useState([]);
  const [tz, setTz] = useState("UTC");
  const [sound, setSound] = useState(soundEnabled());
  const [level, setLevel] = useState(user?.level_alerts !== false);
  const DEFAULT_PLAN = { account_size: 10000, max_risk_per_trade: 0.5, max_daily_loss: 300, max_weekly_loss: 800, max_trades_per_day: 3, max_consecutive_losses: 3 };
  useEffect(() => { api.get("/plan").then(({ data }) => setPlan(data)).catch(() => setPlan(DEFAULT_PLAN)); }, []);
  useEffect(() => {
    setEmailAlerts(!!user?.email_alerts);
    setWeekly(!!user?.weekly_digest);
    if (user?.quiet_hours) setQh({ enabled: false, start: "22:00", end: "07:00", ...user.quiet_hours });
    setSessions(user?.alert_sessions || []);
    setTz(user?.timezone || "UTC");
    setLevel(user?.level_alerts !== false);
  }, [user]);

  const save = async () => {
    try { await api.post("/plan", plan); toast.success("Trading plan updated."); }
    catch { toast.error("Could not save plan."); }
  };
  const resetPaper = async () => {
    try { await api.post("/paper/reset"); toast.success("Paper account reset."); }
    catch { toast.error("Could not reset."); }
  };
  const toggleEmail = async (val) => {
    setEmailAlerts(val);
    try { await api.patch("/me/preferences", { email_alerts: val }); await refreshUser(); toast.success(val ? "Email alerts on." : "Email alerts off."); }
    catch { setEmailAlerts(!val); toast.error("Could not update preference."); }
  };
  const saveQuiet = async () => {
    try { await api.patch("/me/preferences", { quiet_hours: qh, alert_sessions: sessions, timezone: tz }); await refreshUser(); toast.success("Alert schedule saved."); }
    catch { toast.error("Could not save schedule."); }
  };
  const toggleSession = (s) => setSessions((cur) => cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s]);
  const toggleSound = (val) => { setSound(val); setSoundEnabled(val); if (val) playAlertChime(); toast.success(val ? "Sound alerts on." : "Sound alerts off."); };
  const toggleLevel = async (val) => {
    setLevel(val);
    try { await api.patch("/me/preferences", { level_alerts: val }); await refreshUser(); toast.success(val ? "Level-break alerts on." : "Level-break alerts off."); }
    catch { setLevel(!val); toast.error("Could not update preference."); }
  };
  const toggleWeekly = async (val) => {
    setWeekly(val);
    try { await api.patch("/me/preferences", { weekly_digest: val }); await refreshUser(); toast.success(val ? "Weekly digest on." : "Weekly digest off."); }
    catch { setWeekly(!val); toast.error("Could not update preference."); }
  };
  const sendDigestNow = async () => {
    try { const { data } = await api.post("/digest/send"); toast.success(data.ok ? "Digest sent to your email." : "Enable email + verify sender to receive it (digest still generated)."); }
    catch { toast.error("Could not send digest."); }
  };

  if (!plan) return <div className="text-sm text-[var(--text-muted)]">Loading settings…</div>;
  const F = (k, label, step) => (
    <div><label className="text-xs text-[var(--text-sec)]">{label}</label>
      <input type="number" step={step} value={plan[k]} onChange={(e) => setPlan({ ...plan, [k]: Number(e.target.value) })}
        className="mt-1 w-full rounded-lg bg-[var(--surface)] border border-[var(--border2)] px-3 py-2 text-sm outline-none focus:border-aurum tnum" data-testid={`setting-${k}`} /></div>
  );

  return (
    <div className="max-w-3xl space-y-5 animate-fade-up">
      <div className="flex items-center gap-3"><SettingsIcon className="text-aurum" /><h1 className="text-2xl font-bold tracking-tight">Settings</h1></div>
      <Card className="p-5">
        <div className="flex items-center justify-between">
          <div><SectionLabel>Account</SectionLabel><div className="mt-1 text-sm">{user?.name} · {user?.email}</div></div>
          <PlanBadge plan={userPlan} />
        </div>
      </Card>
      <Card className="p-5" data-testid="billing-card">
        <div className="flex items-center gap-2 mb-1"><CreditCard size={16} className="text-aurum" /><SectionLabel>Subscription & Billing</SectionLabel></div>
        <div className="mt-3 flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-sm font-medium flex items-center gap-2">
              Current plan: {ent?.label || "Free"} {userPlan === "ELITE" && <Crown size={14} className="text-aurum" />}
            </div>
            <p className="text-xs text-[var(--text-muted)] max-w-md mt-1">
              {userPlan === "FREE"
                ? "You're on the free plan with full access — live XAU/USD data, analysis, alerts, backtesting and custom strategies. No upgrade required."
                : "Thanks for being a paying member. Manage or change your plan anytime."}
            </p>
          </div>
          <Link to="/pricing" data-testid="manage-billing"
            className={`rounded-full px-5 py-2 text-sm font-semibold transition-all ${userPlan === "FREE" ? "btn-gold hover:brightness-110" : "border border-[var(--border2)] hover:border-aurum hover:text-aurum"}`}>
            {userPlan === "FREE" ? "View plans" : "Change plan"}
          </Link>
        </div>
      </Card>
      <Card className="p-5">
        <div className="flex items-center gap-2 mb-1"><Mail size={16} className="text-aurum" /><SectionLabel>Alert Preferences</SectionLabel></div>
        <div className="mt-3 flex items-center justify-between">
          <div>
            <div className="text-sm font-medium">Email alerts</div>
            <p className="text-xs text-[var(--text-muted)] max-w-md">Get an email for the important statuses (ENTRY CONDITIONS SATISFIED and TRADING LOCKED) even when your browser is closed. Browser push (all statuses) is enabled from the bell icon.</p>
          </div>
          <button onClick={() => toggleEmail(!emailAlerts)} data-testid="toggle-email-alerts"
            role="switch" aria-checked={emailAlerts}
            className={`relative h-6 w-11 rounded-full transition-colors ${emailAlerts ? "bg-aurum" : "bg-[var(--border2)]"}`} aria-label="Toggle email alerts">
            <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${emailAlerts ? "translate-x-5" : "translate-x-0.5"}`} />
          </button>
        </div>
        <p className="mt-2 text-[11px] text-[var(--text-muted)]">Emails are sent from your configured sender address. Verify your domain in Resend for reliable production delivery.</p>
        <div className="mt-4 pt-4 border-t border-[var(--border2)] flex items-center justify-between">
          <div>
            <div className="text-sm font-medium flex items-center gap-1.5"><Volume2 size={14} className="text-aurum" /> Sound alert (while logged in)</div>
            <p className="text-xs text-[var(--text-muted)] max-w-md">Play a chime and pop an on-screen alert the moment a setup triggers while you have the platform open.</p>
          </div>
          <button onClick={() => toggleSound(!sound)} data-testid="toggle-sound-alerts"
            role="switch" aria-checked={sound}
            className={`relative h-6 w-11 rounded-full transition-colors ${sound ? "bg-aurum" : "bg-[var(--border2)]"}`} aria-label="Toggle sound alerts">
            <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${sound ? "translate-x-5" : "translate-x-0.5"}`} />
          </button>
        </div>
        <div className="mt-4 pt-4 border-t border-[var(--border2)] flex items-center justify-between">
          <div>
            <div className="text-sm font-medium">Level-break alerts (Prev-day High / Low)</div>
            <p className="text-xs text-[var(--text-muted)] max-w-md">Get pinged (sound, email + push) the moment gold breaks or retests yesterday's high or low — your live entry triggers off the PDH/PDL lines.</p>
          </div>
          <button onClick={() => toggleLevel(!level)} data-testid="toggle-level-alerts"
            role="switch" aria-checked={level}
            className={`relative h-6 w-11 rounded-full transition-colors ${level ? "bg-aurum" : "bg-[var(--border2)]"}`} aria-label="Toggle level-break alerts">
            <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${level ? "translate-x-5" : "translate-x-0.5"}`} />
          </button>
        </div>
        <div className="mt-4 pt-4 border-t border-[var(--border2)] flex items-center justify-between">
          <div>
            <div className="text-sm font-medium">Weekly digest</div>
            <p className="text-xs text-[var(--text-muted)] max-w-md">A Monday email summarising last week's paper performance, best session and top strategy.</p>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={sendDigestNow} data-testid="send-digest-now" className="text-xs rounded-full border border-aurum text-aurum px-3 py-1.5 hover:bg-[rgba(214,174,87,0.1)] transition-colors">Send me one now</button>
            <button onClick={() => toggleWeekly(!weekly)} data-testid="toggle-weekly-digest" role="switch" aria-checked={weekly}
              className={`relative h-6 w-11 rounded-full transition-colors ${weekly ? "bg-aurum" : "bg-[var(--border2)]"}`} aria-label="Toggle weekly digest">
              <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${weekly ? "translate-x-5" : "translate-x-0.5"}`} />
            </button>
          </div>
        </div>
      </Card>
      <Card className="p-5" data-testid="quiet-hours-card">
        <div className="flex items-center gap-2 mb-1"><Clock size={16} className="text-aurum" /><SectionLabel>Alert Schedule — only reach me while I trade</SectionLabel></div>
        <div className="mt-3 flex items-center justify-between">
          <div className="text-sm font-medium">Quiet hours</div>
          <button onClick={() => setQh({ ...qh, enabled: !qh.enabled })} data-testid="toggle-quiet-hours"
            role="switch" aria-checked={qh.enabled}
            className={`relative h-6 w-11 rounded-full transition-colors ${qh.enabled ? "bg-aurum" : "bg-[var(--border2)]"}`} aria-label="Toggle quiet hours">
            <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${qh.enabled ? "translate-x-5" : "translate-x-0.5"}`} />
          </button>
        </div>
        {qh.enabled && (
          <div className="mt-3 grid grid-cols-3 gap-3">
            <div><label className="text-xs text-[var(--text-sec)]">From</label>
              <input type="time" value={qh.start} onChange={(e) => setQh({ ...qh, start: e.target.value })} className="mt-1 w-full rounded-lg bg-[var(--surface)] border border-[var(--border2)] px-3 py-2 text-sm outline-none focus:border-aurum tnum" data-testid="quiet-start" /></div>
            <div><label className="text-xs text-[var(--text-sec)]">To</label>
              <input type="time" value={qh.end} onChange={(e) => setQh({ ...qh, end: e.target.value })} className="mt-1 w-full rounded-lg bg-[var(--surface)] border border-[var(--border2)] px-3 py-2 text-sm outline-none focus:border-aurum tnum" data-testid="quiet-end" /></div>
            <div><label className="text-xs text-[var(--text-sec)]">Timezone</label>
              <input value={tz} onChange={(e) => setTz(e.target.value)} placeholder="UTC" className="mt-1 w-full rounded-lg bg-[var(--surface)] border border-[var(--border2)] px-3 py-2 text-sm outline-none focus:border-aurum" data-testid="quiet-tz" /></div>
          </div>
        )}
        <div className="mt-4">
          <div className="text-sm font-medium mb-1">Only alert during sessions</div>
          <p className="text-xs text-[var(--text-muted)] mb-2">Leave all off to receive alerts in any session.</p>
          <div className="flex flex-wrap gap-2">
            {["Asian", "London", "New York"].map((s) => (
              <button key={s} onClick={() => toggleSession(s)} data-testid={`session-${s.replace(/\s/g, "-")}`}
                className={`rounded-full px-3 py-1.5 text-xs border transition-colors ${sessions.includes(s) ? "border-aurum text-aurum bg-[rgba(214,174,87,0.08)]" : "border-[var(--border2)] text-[var(--text-sec)]"}`}>{s}</button>
            ))}
          </div>
        </div>
        <button onClick={saveQuiet} data-testid="save-quiet" className="mt-4 btn-gold rounded-full px-5 py-2 text-sm font-semibold hover:brightness-110">Save schedule</button>
        <p className="mt-2 text-[11px] text-[var(--text-muted)]">During quiet hours or outside your chosen sessions, push & email are muted — alerts still appear in your in-app history so nothing is lost.</p>
      </Card>
      <Card className="p-5">
        <SectionLabel>My Trading Plan (Risk Limits)</SectionLabel>
        <div className="mt-4 grid grid-cols-2 gap-4">
          {F("account_size", "Account size ($)")}
          {F("max_risk_per_trade", "Max risk / trade (%)", "0.1")}
          {F("max_daily_loss", "Max daily loss ($)")}
          {F("max_weekly_loss", "Max weekly loss ($)")}
          {F("max_trades_per_day", "Max trades / day")}
          {F("max_consecutive_losses", "Max consecutive losses")}
        </div>
        <button onClick={save} data-testid="save-plan" className="mt-4 btn-gold rounded-full px-5 py-2 text-sm font-semibold hover:brightness-110">Save plan</button>
      </Card>
      <Card className="p-5 flex items-center justify-between">
        <div><SectionLabel>Paper Account</SectionLabel><p className="mt-1 text-xs text-[var(--text-muted)]">Reset balance and clear all simulated trades.</p></div>
        <button onClick={resetPaper} data-testid="reset-paper" className="flex items-center gap-2 rounded-full border border-[var(--border2)] px-4 py-2 text-sm hover:border-bear hover:text-bear transition-colors"><RefreshCw size={15} /> Reset</button>
      </Card>
    </div>
  );
}
